import { Grid, InputAdornment } from "@mui/material";
import type { FC, ReactNode } from "react";
import type { Control } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import FormCheckbox from "../../components/formComponents/FormCheckbox";
import FormInfoEdit from "../../components/formComponents/FormInfoEdit";
import FormNumberField from "../../components/formComponents/FormNumberField";
import FormSwitch from "../../components/formComponents/FormSwitch";
import FormTextField from "../../components/formComponents/FormTextField";
import { Select } from "../../components/select/Select";
import CostCurrencySelect from "../../costs/CostCurrencySelect";
import type { CostCurrency } from "../../costs/costApi";
import ContactPersons from "./ContactPersons";
import ParticipantListRequiredFieldsSelect from "./ParticipantListRequiredFieldsSelect";
import { providerColor } from "./providerColor";
import type { ParticipantListRequiredFields, PriceMargin } from "./providersApi";
import { ProviderProductType } from "./providersApi";

interface ProductTypeSelectProps {
  readonly productType: ProviderProductType;
  readonly onChange: (t: ProviderProductType) => void;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string;
}

const ProductTypeSelect: FC<ProductTypeSelectProps> = ({ productType, onChange, disabled, error, helperText }) => {
  const productTypes = [
    {
      value: ProviderProductType.WATERBORNE_TRANSPORT_VAT_NP,
      label: "Transport morski (VAT np)",
    },
    {
      value: ProviderProductType.WATERBORNE_TRANSPORT_VAT_0,
      label: "Transport morski (VAT 0%)",
    },
    {
      value: ProviderProductType.HOTEL,
      label: "Hotel Polska (VAT 8%)",
    },
    {
      value: ProviderProductType.HOTEL_VAT_NP,
      label: "Hotel (VAT np)",
    },
    {
      value: ProviderProductType.ATTRACTION,
      label: "Atrakcja (VAT np)",
    },
    {
      value: ProviderProductType.PLANE_TICKET_VAT_NP,
      label: "Bilet lotniczy (VAT np)",
    },
    {
      value: ProviderProductType.PLANE_TICKET_VAT_0,
      label: "Bilet lotniczy (VAT 0%)",
    },
    {
      value: ProviderProductType.BUS_TRANSPORT_VAT_NP,
      label: "Transport autokarowy (VAT np)",
    },
    {
      value: ProviderProductType.BUS_TRANSPORT_VAT_8,
      label: "Transport autokarowy (VAT 8%)",
    },
  ];

  return (
    <Select
      label="Rodzaj produktu"
      fullWidth
      values={productTypes}
      value={productType}
      onSelect={onChange}
      disabled={disabled}
      error={error}
      helperText={helperText}
    />
  );
};

export interface ProviderFormState {
  name: string;
  contact: {
    phone: string;
    email: string;
  };
  info: string;
  termsAndConditions: string;
  description: { polish?: string; english?: string };
  productType: ProviderProductType | null;
  freeCancellationDays: number | null;
  contractNumber: string;
  contacts: {
    name: string;
    phone: string;
    email: string;
    loginEmail: string;
    position: string;
    emailsRecipient: boolean;
  }[];
  taxInfo: {
    taxNo: string;
    useForAllProducts: boolean;
    skipValidation: boolean;
  };
  participantListRequiredFields: ParticipantListRequiredFields;
  useDynamicPrices: boolean;
  currency: CostCurrency | null;
  priceMargin: PriceMargin;
  computePricesFromPurchasePrice: boolean;
}

interface Props {
  readonly control: Control<ProviderFormState>;
  readonly files?: ReactNode;
  readonly disabled?: boolean;
}

const ProviderEdit: FC<Props> = ({ control, files, disabled }) => {
  const name = useWatch({ control, name: "name" });
  return (
    <Grid size={12} container spacing={2}>
      <Grid container size={{ xs: 12, lg: 6 }} spacing={2}>
        <Grid size={12}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormTextField
                label="Nazwa"
                name="name"
                autoFocus
                control={control}
                disabled={disabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ ...providerColor({ name }) }}>
                      <div style={{ padding: 4 }}>@</div>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={6}>
              <FormTextField label="Telefon" name="contact.phone" control={control} disabled={disabled} />
            </Grid>
            <Grid size={6}>
              <FormTextField label="Mail" name="contact.email" control={control} disabled={disabled} />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={6}>
          <FormTextField label="NIP" name="taxInfo.taxNo" control={control} disabled={disabled} />
        </Grid>
        <Grid
          size={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: 56,
          }}
        >
          <FormCheckbox
            name="taxInfo.useForAllProducts"
            control={control}
            label="Zastosuj do wszystkich produktów"
            size="small"
            disabled={disabled}
          />
          <FormCheckbox
            name="taxInfo.skipValidation"
            control={control}
            label="Pomiń walidację NIP"
            size="small"
            disabled={disabled}
          />
        </Grid>
        <Grid size={12}>
          <FormTextField label="Contract number" name="contractNumber" control={control} disabled={disabled} />
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12, lg: 6 }} spacing={2}>
        <Grid size={12}>
          <FormNumberField
            label="Liczba dni bezkosztowej anulacji"
            name="freeCancellationDays"
            control={control}
            disabled={disabled}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            name="productType"
            control={control}
            rules={{ required: "Rodzaj produktu jest wymagany" }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ProductTypeSelect
                productType={value as ProviderProductType}
                onChange={onChange}
                disabled={disabled}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            name="currency"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CostCurrencySelect value={value} onChange={onChange} disabled={disabled} />
            )}
          />
        </Grid>
        <Grid size={6}>
          <FormNumberField name="priceMargin.multiplier" control={control} label="Mnożnik marży" disabled={disabled} />
        </Grid>
        <Grid size={6}>
          <FormNumberField name="priceMargin.amount" control={control} label="Kwota marży" disabled={disabled} />
        </Grid>
      </Grid>
      <Grid size={12}>
        <ContactPersons control={control} disabled={disabled} />
      </Grid>
      {files}
      <Grid size={12}>
        <ParticipantListRequiredFieldsSelect control={control} disabled={disabled} />
      </Grid>
      <Grid size={6}>
        <FormSwitch label="Używaj cen dynamicznych" name="useDynamicPrices" control={control} disabled={disabled} />
      </Grid>
      <Grid size={6}>
        <FormSwitch
          label="Licz ceny z marży (na podstawie cennika zakupu)"
          name="computePricesFromPurchasePrice"
          control={control}
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12, xl: 6 }}>
        <FormInfoEdit label="Info" name="info" control={control} disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, xl: 6 }}>
        <FormInfoEdit label="Terms & conditions" name="termsAndConditions" control={control} disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, xl: 6 }}>
        <FormInfoEdit label="Opis WWW" name="description.polish" control={control} disabled={disabled} />
      </Grid>
      <Grid size={{ xs: 12, xl: 6 }}>
        <FormInfoEdit label="Opis WWW (eng)" name="description.english" control={control} disabled={disabled} />
      </Grid>
    </Grid>
  );
};

export default ProviderEdit;
